import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

export function PaymentSuccess() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setStatus("paid"); return; }
        if (res.data.status === "expired" || res.data.payment_status === "failed") { setStatus("error"); return; }
      } catch (e) { console.error("Payment status poll error:", e); }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 text-center">
      <div className="max-w-md" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto mb-6 animate-spin text-primary" />
            <h1 className="font-display text-2xl font-bold">{t("processing")}</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="grid place-items-center h-16 w-16 rounded-2xl bg-emerald-500/10 text-emerald-600 mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="font-display text-3xl font-extrabold mb-3">{t("pay_success")}</h1>
            <p className="text-muted-foreground mb-8">{t("pay_success_sub")}</p>
            <Button data-testid="success-dashboard-btn" onClick={() => navigate("/dashboard")}>{t("go_dash")}</Button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <div className="grid place-items-center h-16 w-16 rounded-2xl bg-destructive/10 text-destructive mx-auto mb-6">
              <XCircle className="h-8 w-8" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-3">{t("pay_cancel")}</h1>
            <Button variant="outline" onClick={() => navigate("/pricing")}>{t("view_pricing")}</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const navigate = useNavigate();
  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 text-center">
      <div className="max-w-md" data-testid="payment-cancel-page">
        <div className="grid place-items-center h-16 w-16 rounded-2xl bg-destructive/10 text-destructive mx-auto mb-6">
          <XCircle className="h-8 w-8" />
        </div>
        <h1 className="font-display text-3xl font-extrabold mb-3">{t("pay_cancel")}</h1>
        <p className="text-muted-foreground mb-8">{t("pay_cancel_sub")}</p>
        <Button onClick={() => navigate("/pricing")} data-testid="cancel-pricing-btn">{t("view_pricing")}</Button>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const { setSessionUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/login"); return; }
    const sessionId = match[1];
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        setSessionUser(res.data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: res.data.user } });
      } catch {
        navigate("/login");
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  );
}
